import AddIcon from "@mui/icons-material/Add";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  TablePagination,
  TextField,
  Chip,
} from "@mui/material";
import React, { useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { getPatients } from "../../../api/services/api";
import { Patient, PatientsResponse } from "../../../api/types/api.types";
import { ViewButton } from "../../../components/IconButtons";
import Table from "../../../components/TableComponent";
import textCapitalization from "../../../components/textCapitalization";
import {
  PageLayout,
  EnhancedEmptyScreen,
  TableSkeleton,
} from "../../../components/Enhanced";
import CreatePatientDialog from "./CreatePatientDialog";

const Patients = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [search, setSearch] = useState("");
  const [gender, setGender] = useState<string>("");
  const [age, setAge] = useState<string>("");
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading } = useQuery<PatientsResponse>(
    [
      "patients",
      {
        search: search,
        limit: limit,
        offset: limit * page,
        gender: gender,
        age: age,
      },
    ],
    getPatients
  );

  console.log(JSON.stringify(data?.data));

  const openDialog = Boolean(anchorEl);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: any) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleClearFilter = () => {
    setAge("");
    setGender("");
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "users.fullName",
      key: "users.fullName",
    },

    {
      title: "Age",
      dataIndex: "age",
      key: "age",
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
      render: (colData: string) => {
        return textCapitalization(colData || "");
      },
    },

    {
      title: "Mobile Number",
      dataIndex: "users.mobileNumber",
      key: "users.mobileNumber",
    },
    {
      title: "Email Address",
      dataIndex: "users",
      key: "users",
      render: (colData: Patient["users"]) => {
        return colData?.email ? colData?.email : "N/A";
      },
    },

    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_colData: any, row: Patient) => (
        <ViewButton
          onClick={() => {
            navigate(`/patients/details/${row?.id}/dashboard`);
          }}
        />
      ),
    },
  ];

  const genderData = ["MALE", "FEMALE", "OTHERS"];

  const ageData = ["1-10", "11-20", "21-40", "41-60", ">60"];

  return (
    <PageLayout
      title="Patients"
      subtitle="Manage and view all registered patients"
      breadcrumbs={[
        { label: "Dashboard", path: "/admin/dashboard" },
        { label: "Patients" },
      ]}
    >
      {/* Search and Filter Bar */}
      <Box display="flex" gap={2} mb={3} alignItems="center">
        <TextField
          label="Search by Patient Name"
          sx={{ flex: 1, maxWidth: 500 }}
          InputProps={{
            endAdornment: (
              <IconButton>
                <SearchIcon />
              </IconButton>
            ),
          }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <Button
          variant="outlined"
          startIcon={<FilterListOutlinedIcon />}
          onClick={handleClick}
        >
          Filter
        </Button>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ ml: "auto" }}
          onClick={() => setCreateOpen(true)}
        >
          Add Patient
        </Button>

        {(gender || age) && (
          <Box display="flex" gap={1} alignItems="center">
            {gender && (
              <Chip
                label={`Gender: ${textCapitalization(gender)}`}
                onDelete={() => setGender("")}
                color="primary"
                variant="outlined"
              />
            )}
            {age && (
              <Chip
                label={`Age: ${age}`}
                onDelete={() => setAge("")}
                color="primary"
                variant="outlined"
              />
            )}
            <Button
              size="small"
              onClick={handleClearFilter}
              sx={{ textTransform: "none" }}
            >
              Clear All
            </Button>
          </Box>
        )}

        <Menu
          open={openDialog}
          onClose={handleClose}
          anchorEl={anchorEl}
          anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        >
          <MenuItem sx={{ width: "350px" }}>
            <Box sx={{ width: "100%" }}>
              <Autocomplete
                options={genderData}
                value={gender}
                getOptionLabel={(option) => textCapitalization(option)}
                onChange={(event, newValue) => {
                  setGender(newValue || "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    label="Select Gender"
                  />
                )}
              />
            </Box>
          </MenuItem>
          <MenuItem>
            <Box sx={{ width: "100%" }}>
              <Autocomplete
                options={ageData}
                getOptionLabel={(option) => option}
                value={age}
                onChange={(event, newValue) => {
                  setAge(newValue?.split(">").join("") || "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    label="Select Age"
                  />
                )}
              />
            </Box>
          </MenuItem>
        </Menu>
      </Box>

      {/* Table or Empty State */}
      {isLoading ? (
        <TableSkeleton rows={10} columns={6} />
      ) : data?.data?.count === 0 ? (
        <EnhancedEmptyScreen
          type="patients"
          message={
            search || gender || age
              ? "No patients found matching your filters. Try adjusting your search criteria."
              : undefined
          }
        />
      ) : (
        <>
          <Table
            key={"Patients"}
            dataSource={data?.data?.patients}
            rowKey={"id"}
            columns={columns}
            loading={isLoading}
          />
          <Box
            sx={{
              marginTop: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TablePagination
              count={data?.data?.count || 0}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={limit}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        </>
      )}

      <CreatePatientDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </PageLayout>
  );
};

export default Patients;
